import { getOfficeToken } from "../lib/sso";
import { fetchMe, fetchSignature, } from "../lib/api";
import { applySignature, getCurrentFromAddress } from "../lib/apply";
import { createApplyRunner, fetchAndReconcile } from "../lib/applyFlow";
import { saveLastGood } from "../lib/cache";
import { formatTiming, loadTiming } from "../lib/timing";
import { clearProfileOverrides, loadProfileOverrides, pruneOverrides, saveProfileOverrides, } from "../lib/profile";
const BRANDS = [
    { shortName: "THF", label: "THF Holdings", logo: "brands/thf-light.png" },
    { shortName: "CCD", label: "CCD", logo: "brands/ccd-light.png" },
    { shortName: "LLC", label: "Lower Lodge", logo: "brands/llc-light.png" },
    { shortName: "CYC", label: "CYC:D", logo: "brands/cyc-light.png" },
    { shortName: "COL", label: "Colorlites", logo: "brands/col-light.png" },
    { shortName: "CB", label: "Coloured Bottles", logo: "brands/cb-light.png" },
    { shortName: "JFD", label: "JFD", logo: "brands/jfd-light.png" },
];
const PROFILE_FIELD_IDS = {
    displayName: "p-displayName",
    jobTitle: "p-jobTitle",
    signOff: "p-signOff",
    mobilePhone: "p-mobilePhone",
    businessPhone: "p-businessPhone",
    streetAddress: "p-streetAddress",
    city: "p-city",
    state: "p-state",
    postalCode: "p-postalCode",
};
const state = {
    selectedBrand: null,
    autoBrand: null,
    forceMhfa: false,
    forceDisclaimer: false,
    forceTuv: false,
    isAdmin: false,
    availableBrands: [],
    me: null,
    overrides: {},
};
function $(id) {
    const el = document.getElementById(id);
    if (!el)
        throw new Error(`#${id} not found`);
    return el;
}
function $input(id) {
    return $(id);
}
function clear(el) {
    while (el.firstChild)
        el.removeChild(el.firstChild);
}
function setStatus(msg, variant = "") {
    const el = $("status");
    let text = el.querySelector(".status-text");
    if (!text) {
        clear(el);
        text = document.createElement("span");
        text.className = "status-text";
        el.appendChild(text);
    }
    text.textContent = msg;
    el.className = `status${variant ? " " + variant : ""}`;
}
function initials(name) {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join("");
}
function renderIdentity(displayName, jobTitle, mail) {
    $("identity-name").textContent = displayName;
    $("identity-job").textContent = jobTitle ?? "";
    $("identity-mail").textContent = mail;
    $("identity-initials").textContent = initials(displayName);
}
function autoLabel() {
    return `Auto${state.autoBrand ? ` (${state.autoBrand})` : ""}`;
}
function makeBrandCard(opts) {
    const card = document.createElement("div");
    card.className = `brand-card${opts.isAuto ? " auto" : ""}`;
    card.dataset.brand = opts.brand;
    if (opts.logoSrc) {
        const img = document.createElement("img");
        img.src = opts.logoSrc;
        img.alt = opts.label;
        card.appendChild(img);
    }
    else if (opts.iconChar) {
        const icon = document.createElement("div");
        icon.style.fontSize = "18px";
        icon.style.lineHeight = "1";
        icon.textContent = opts.iconChar;
        card.appendChild(icon);
    }
    const labelEl = document.createElement("div");
    labelEl.className = "brand-card-label";
    labelEl.textContent = opts.label;
    card.appendChild(labelEl);
    card.addEventListener("click", opts.onClick);
    return card;
}
function renderBrandCards() {
    const grid = $("brand-grid");
    clear(grid);
    // Show only brands the user can actually send from — a brand whose domain
    // is their primary, an alias/proxy address, or (future) a shared mailbox
    // they can send-as. availableBrands is computed server-side from the Graph
    // profile. Applies to admins too: the panel is a personal signature tool,
    // not a brand previewer (that's the admin web app).
    const brandsToShow = BRANDS.filter((b) => state.availableBrands.includes(b.shortName));
    // Nothing to choose between (one or zero owned brands) → hide the section.
    if (brandsToShow.length <= 1) {
        $("brand-section").classList.add("hidden");
        return;
    }
    $("brand-section").classList.remove("hidden");
    grid.appendChild(makeBrandCard({
        brand: "",
        label: autoLabel(),
        isAuto: true,
        iconChar: "⚡",
        onClick: () => selectBrand(null),
    }));
    for (const b of brandsToShow) {
        grid.appendChild(makeBrandCard({
            brand: b.shortName,
            label: b.label,
            logoSrc: b.logo,
            onClick: () => selectBrand(b.shortName),
        }));
    }
    highlightSelected();
}
function highlightSelected() {
    const cards = document.querySelectorAll(".brand-card");
    for (const c of Array.from(cards)) {
        const brand = c.dataset.brand ?? "";
        const isSelected = brand === (state.selectedBrand ?? "");
        c.classList.toggle("selected", isSelected);
    }
    const autoCard = document.querySelector('.brand-card[data-brand=""]');
    if (autoCard) {
        const labelEl = autoCard.querySelector(".brand-card-label");
        if (labelEl)
            labelEl.textContent = autoLabel();
    }
}
function selectBrand(shortName) {
    state.selectedBrand = shortName;
    highlightSelected();
    scheduleApply();
}
let applyTimer = null;
const APPLY_DEBOUNCE_MS = 350;
function scheduleApply() {
    if (applyTimer)
        clearTimeout(applyTimer);
    setStatus("Pending…", "busy");
    applyTimer = setTimeout(() => {
        applyTimer = null;
        void runApply();
    }, APPLY_DEBOUNCE_MS);
}
const runApply = createApplyRunner(() => fetchAndApply());
function readProfileFromInputs() {
    const o = {};
    for (const [key, id] of Object.entries(PROFILE_FIELD_IDS)) {
        const v = $input(id).value;
        if (v != null)
            o[key] = v;
    }
    return o;
}
function applyProfileToInputs(o) {
    for (const [key, id] of Object.entries(PROFILE_FIELD_IDS)) {
        $input(id).value = o[key] ?? "";
    }
}
function applyPlaceholdersFromMe(me) {
    const map = {
        "p-displayName": me.displayName,
        "p-jobTitle": me.jobTitle,
        "p-mobilePhone": me.mobilePhone,
        "p-businessPhone": me.businessPhone,
        "p-streetAddress": me.streetAddress,
        "p-city": me.city,
        "p-state": me.state,
        "p-postalCode": me.postalCode,
    };
    for (const [id, val] of Object.entries(map)) {
        const el = $input(id);
        if (el.tagName === "INPUT")
            el.placeholder = val ?? "";
    }
}
async function fetchAndApply() {
    const item = Office.context.mailbox.item;
    if (!item) {
        setStatus("Open a new mail or reply to apply.", "error");
        return;
    }
    setStatus("Fetching signature…", "busy");
    const overrides = pruneOverrides(state.overrides);
    // Same from-address the LaunchEvent runtime uses, so the brand resolves for
    // the address being composed from AND the last-good cache is keyed the
    // way commands.ts reads it back (a null-keyed entry never matched).
    const fromAddress = await getCurrentFromAddress(item);
    let payload;
    let automatic;
    try {
        ({ payload, automatic } = await fetchAndReconcile(state, overrides, fromAddress, { getToken: getOfficeToken, fetchSignature }));
    }
    catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setStatus(`Fetch failed: ${msg}`, "error");
        return;
    }
    highlightSelected();
    setStatus("Applying…", "busy");
    try {
        await applySignature(item, payload);
        await saveLastGood(payload, fromAddress, automatic);
        void saveProfileOverrides(state.overrides);
        setStatus(`Applied ${payload.brand.name}${payload.mhfa ? " + MHFA" : ""}.`, "success");
    }
    catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setStatus(`Apply failed: ${msg}`, "error");
    }
}
const ME_KEY = "thf.me";
function applyMe(me) {
    state.me = me;
    state.isAdmin = me.isAdmin;
    state.autoBrand = me.brand.shortName;
    state.availableBrands = me.availableBrands;
    renderIdentity(me.displayName, me.jobTitle, me.mail);
    applyPlaceholdersFromMe(me);
    $("admin-section").classList.toggle("hidden", !me.isAdmin);
    renderBrandCards();
}
async function initFromMe() {
    // Cheap identity from Office context first (no round-trip)
    const profile = Office.context.mailbox.userProfile;
    renderIdentity(profile.displayName ?? profile.emailAddress, null, profile.emailAddress);
    // Paint the last /api/me straight away (same mailbox only); the live call
    // below refreshes it. Saves the token + api + Graph wait on every open.
    try {
        const saved = JSON.parse(localStorage.getItem(ME_KEY) ?? "null");
        if (saved &&
            saved.mail?.toLowerCase() === profile.emailAddress.toLowerCase()) {
            applyMe(saved);
        }
    }
    catch {
        // storage unavailable or corrupt — fall through to the live call
    }
    // Load any saved profile overrides
    state.overrides = await loadProfileOverrides();
    applyProfileToInputs(state.overrides);
    try {
        const token = await getOfficeToken();
        const me = await fetchMe(token);
        applyMe(me);
        try {
            localStorage.setItem(ME_KEY, JSON.stringify(me));
        }
        catch {
            // not fatal — next open just waits on the live call
        }
    }
    catch (err) {
        // SSO unavailable (e.g. dev sideload) — keep non-admin defaults, but say
        // so: a silent "Ready." with no brand buttons reads as a broken panel.
        const msg = err instanceof Error ? err.message : String(err);
        console.warn("[taskpane] /api/me failed:", err);
        setStatus(`Couldn't load your profile: ${msg}`, "error");
        renderBrandCards();
    }
}
function bindThemeToggle() {
    const btn = $("theme-toggle");
    const root = document.documentElement;
    const apply = (dark) => {
        root.classList.toggle("dark", dark);
        btn.textContent = dark ? "☾" : "☀";
    };
    // Default is dark (class set in HTML); honour a saved preference.
    try {
        if (localStorage.getItem("thf.theme") === "light")
            apply(false);
        else
            apply(root.classList.contains("dark"));
    }
    catch {
        apply(root.classList.contains("dark"));
    }
    btn.addEventListener("click", () => {
        const dark = !root.classList.contains("dark");
        apply(dark);
        try {
            localStorage.setItem("thf.theme", dark ? "dark" : "light");
        }
        catch {
            // storage unavailable — theme still toggles for the session
        }
    });
}
function bindAdminToggle() {
    const toggle = $("admin-toggle");
    const tools = $("admin-tools");
    const icon = $("admin-toggle-icon");
    toggle.addEventListener("click", () => {
        const open = tools.classList.toggle("open");
        icon.textContent = open ? "▴" : "▾";
    });
}
function bindProfileEditor() {
    for (const [key, id] of Object.entries(PROFILE_FIELD_IDS)) {
        const el = $input(id);
        const eventName = el.tagName === "SELECT" ? "change" : "input";
        el.addEventListener(eventName, () => {
            state.overrides = { ...state.overrides, [key]: el.value };
            // Save immediately so values persist even if the apply step fails or
            // the user closes the taskpane before debounced apply fires.
            void saveProfileOverrides(state.overrides);
            scheduleApply();
        });
    }
    $("reset-profile").addEventListener("click", async () => {
        state.overrides = {};
        applyProfileToInputs({});
        await clearProfileOverrides();
        scheduleApply();
    });
}
async function showLastTiming() {
    const t = await loadTiming();
    if (t)
        $("last-timing").textContent = formatTiming(t);
}
Office.onReady(() => {
    void showLastTiming();
    bindThemeToggle();
    bindAdminToggle();
    bindProfileEditor();
    void initFromMe();
    $input("mhfa").addEventListener("change", (e) => {
        state.forceMhfa = e.target.checked;
        scheduleApply();
    });
    $input("disclaimer").addEventListener("change", (e) => {
        state.forceDisclaimer = e.target.checked;
        scheduleApply();
    });
    $input("tuv").addEventListener("change", (e) => {
        state.forceTuv = e.target.checked;
        scheduleApply();
    });
    $("apply").addEventListener("click", () => {
        void runApply();
    });
    setStatus("Ready.");
});
